import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UpcomingActivity {
  id: string;
  type: "booking" | "open_match" | "tournament" | "training";
  title: string;
  subtitle: string;
  startTime: Date;
  endTime?: Date;
  location?: string;
  pinCode?: string;
  linkTo: string;
  icon: "racket" | "trophy" | "swords" | "dumbbell";
  badge?: string;
  participants?: string[];
}

interface BookingWithCourt {
  id: string;
  start_time: string;
  end_time: string;
  pin_code: string | null;
  is_open_match: boolean;
  courts: { name: string; halls: { name: string } | null } | null;
}

interface BookingPlayerWithBooking {
  booking_id: string;
  bookings: {
    id: string;
    start_time: string;
    end_time: string;
    pin_code: string | null;
    user_id: string;
    is_open_match: boolean;
    status: string;
    courts: { name: string; halls: { name: string } | null } | null;
  } | null;
}

interface OpenMatchPlayerWithMatch {
  activity_id: string;
  // SPÅR 2a: table open_matches → activities. PostgREST embed-relation
  // follows the new table name.
  activities: {
    id: string;
    title: string | null;
    date: string;
    start_time: string;
    end_time: string;
    match_type: string;
    status: string;
    max_players: number;
    current_players: number;
  } | null;
}

interface TournamentPlayerWithTournament {
  tournament_id: string;
  tournaments: {
    id: string;
    name: string;
    date: string;
    format: string;
    status: string;
  } | null;
}

interface SessionAttendanceWithSession {
  session_id: string;
  training_sessions: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    training_groups: { name: string } | null;
  } | null;
}

export function useUpcomingActivities() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["upcoming-activities", user?.id],
    queryFn: async (): Promise<UpcomingActivity[]> => {
      const now = new Date().toISOString();
      const today = new Date().toISOString().split("T")[0];
      const activities: UpcomingActivity[] = [];

      // 1. Own bookings
      const { data: ownBookings } = await supabase
        .from("bookings")
        .select("id, start_time, end_time, pin_code, is_open_match, courts(name, halls(name))")
        .eq("user_id", user!.id)
        .eq("status", "confirmed")
        .gte("start_time", now)
        .order("start_time", { ascending: true })
        .limit(10);

      for (const raw of ownBookings ?? []) {
        const b = raw as unknown as BookingWithCourt;
        activities.push({
          id: `booking-${b.id}`,
          type: "booking",
          title: b.courts?.name ?? "Bana",
          subtitle: b.courts?.halls?.name ?? "",
          startTime: new Date(b.start_time),
          endTime: b.end_time ? new Date(b.end_time) : undefined,
          location: b.courts?.halls?.name,
          pinCode: b.pin_code ?? undefined,
          linkTo: "/bookings",
          icon: b.is_open_match ? "swords" : "racket",
          badge: b.is_open_match ? "Aktivitet" : undefined,
        });
      }

      // 2. Bookings where user is a participant (not owner)
      const { data: participantBookings } = await supabase
        .from("booking_players")
        .select("booking_id, bookings(id, start_time, end_time, pin_code, user_id, is_open_match, status, courts(name, halls(name)))")
        .eq("user_id", user!.id)
        .gte("bookings.start_time", now);

      for (const raw of participantBookings ?? []) {
        const bp = raw as unknown as BookingPlayerWithBooking;
        const b = bp.bookings;
        if (!b || b.status !== "confirmed" || b.user_id === user!.id) continue;
        // Avoid duplicates
        if (activities.some((a) => a.id === `booking-${b.id}`)) continue;
        activities.push({
          id: `booking-${b.id}`,
          type: "booking",
          title: b.courts?.name ?? "Bana",
          subtitle: b.courts?.halls?.name ?? "",
          startTime: new Date(b.start_time),
          endTime: b.end_time ? new Date(b.end_time) : undefined,
          location: b.courts?.halls?.name,
          pinCode: b.pin_code ?? undefined,
          linkTo: "/bookings",
          icon: b.is_open_match ? "swords" : "racket",
          badge: b.is_open_match ? "Aktivitet" : "Inbjuden",
        });
      }

      // 3. Activities user has joined
      // SPÅR 2a: open_matches table renamed to activities. PostgREST
      // embed-relation now expects the new table name.
      const { data: joinedMatches } = await supabase
        .from("activity_players")
        .select("activity_id, activities(id, title, date, start_time, end_time, match_type, status, max_players, current_players)")
        .eq("user_id", user!.id);

      for (const raw of joinedMatches ?? []) {
        const omp = raw as unknown as OpenMatchPlayerWithMatch;
        const om = omp.activities;
        if (!om || om.status === "cancelled" || om.date < today) continue;
        activities.push({
          id: `open-match-${om.id}`,
          type: "open_match",
          title: om.title || "Aktivitet",
          subtitle: `${om.current_players}/${om.max_players} spelare`,
          startTime: new Date(`${om.date}T${om.start_time}`),
          endTime: om.end_time ? new Date(`${om.date}T${om.end_time}`) : undefined,
          linkTo: "/play",
          icon: "swords",
          badge: om.match_type !== "match" ? om.match_type : undefined,
        });
      }

      // 4. Tournaments user is registered for
      const { data: tournaments } = await supabase
        .from("tournament_players")
        .select("tournament_id, tournaments(id, name, date, format, status)")
        .eq("user_id", user!.id);

      for (const raw of tournaments ?? []) {
        const tp = raw as unknown as TournamentPlayerWithTournament;
        const t = tp.tournaments;
        if (!t || t.date < today || !["registration", "active"].includes(t.status)) continue;
        activities.push({
          id: `tournament-${t.id}`,
          type: "tournament",
          title: t.name,
          subtitle: t.format || "Turnering",
          startTime: new Date(t.date),
          linkTo: `/tournaments/${t.id}`,
          icon: "trophy",
          badge: t.status === "registration" ? "Anmälan öppen" : "Pågår",
        });
      }

      // 5. Training sessions user is attending
      const { data: trainingSessions } = await supabase
        .from("session_attendance")
        .select("session_id, training_sessions(id, date, start_time, end_time, training_groups(name))")
        .eq("user_id", user!.id);

      for (const raw of trainingSessions ?? []) {
        const sa = raw as unknown as SessionAttendanceWithSession;
        const ts = sa.training_sessions;
        if (!ts || ts.date < today) continue;
        activities.push({
          id: `training-${ts.id}`,
          type: "training",
          title: ts.training_groups?.name || "Träningspass",
          subtitle: `${ts.start_time}–${ts.end_time}`,
          startTime: new Date(`${ts.date}T${ts.start_time}`),
          endTime: ts.end_time ? new Date(`${ts.date}T${ts.end_time}`) : undefined,
          linkTo: "/training",
          icon: "dumbbell",
        });
      }

      // Sort by startTime and limit to 5
      activities.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      return activities.slice(0, 5);
    },
    enabled: !!user?.id,
  });
}
